import { Type, type Static } from '@sinclair/typebox';
import { IsoDateTimeSchema } from '../../shared/utils/date-time.schema';
import { PaginationQueryParamsSchema } from '../../shared/utils/pagination-query-params.schema';
import { StringEnum } from '../../utils/typebox';
import {
  CalendarEventConferenceRequestSchema,
  CalendarEventDateSchema,
  CalendarEventDateTimeSchema,
} from './calendar-event.schema';

export const GetCalendarEventListParamsSchema = Type.Object({
  calendar_id: Type.String({ minLength: 1, description: 'Identifier of the calendar.' }),
});

export const GetCalendarEventListQuerySchema = Type.Intersect([
  Type.Object({
    is_cancelled: Type.Optional(
      Type.Boolean({ default: true, description: 'Whether you want to retrieve the cancelled events.' }),
    ),
    title: Type.Optional(Type.String({ description: 'Filter for events matching the specified title.' })),
    description: Type.Optional(Type.String({ description: 'Filter for events matching the specified description.' })),
    ical_uid: Type.Optional(Type.String({ description: 'Filter for events matching the specified iCal UID.' })),
    location: Type.Optional(Type.String({ description: 'Filter for events matching the specified location.' })),
    start: Type.Optional(
      IsoDateTimeSchema({
        description: 'Filter events starting after this date.',
      }),
    ),
    end: Type.Optional(
      IsoDateTimeSchema({
        description: 'Filter events ending before this date.',
      }),
    ),
    busy: Type.Optional(
      Type.Boolean({ description: 'Filter for events blocking time on the calendar, i.e. whose `transparency` is `opaque`.' }),
    ),
    updated_before: Type.Optional(IsoDateTimeSchema({ description: 'Filter events updated before this date.' })),
    updated_after: Type.Optional(IsoDateTimeSchema({ description: 'Filter events updated after this date.' })),
    attendees: Type.Optional(
      Type.String({
        description:
          'Filter events with attendees contained in this list. This parameter accepts a comma-delimited list of email addresses.',
      }),
    ),
    event_type: Type.Optional(
      Type.String({ description: 'Filter events by event type. This parameter accepts a comma-delimited list of event type.' }),
    ),
    expand_recurring: Type.Optional(Type.Boolean({ description: 'Filter events by type (single or occurence).' })),
  }),
  PaginationQueryParamsSchema,
]);

export const GetCalendarEventParamsSchema = Type.Object({
  calendar_id: Type.String({ minLength: 1, description: 'Identifier of the parent calendar.' }),
  event_id: Type.String({ minLength: 1, description: 'Identifier of the event to retrieve ' }),
});

export const CalendarEventAttendeeInputSchema = Type.Object(
  {
    email: Type.String({ description: 'Email of the attendee.' }),
    type: Type.Optional(
      StringEnum(['required', 'optional'], {
        description: `The type of the attendee.
            - \`required\` the attendance of the attendee is required.
            - \`optional\` the attendance of the attendee is optional.`,
        default: 'required',
      }),
    ),
  },
  { description: 'An attendee to invite to the event.', additionalProperties: false },
);

export const CreateCalendarEventParamsSchema = Type.Object({
  calendar_id: Type.String({ minLength: 1, description: 'Identifier of the parent calendar.' }),
});

export const CreateCalendarEventBodySchema = Type.Object(
  {
    title: Type.String({ description: 'The title of the event.' }),
    body: Type.Optional(Type.String({ description: 'The body of the event.' })),
    location: Type.Optional(Type.String({ description: 'The location of the event.' })),
    background_color: Type.Optional(
      Type.String({
        description: 'Background color of the calendar in hexadecimal format. For Google we match the closest palette available',
      }),
    ),
    transparency: Type.Optional(
      StringEnum(['opaque', 'transparent'], {
        description: `The transparency of the event.
            - \`opaque\` does block time on the calendar and is equivalent to setting Show me as to Busy in the Calendar UI.
            - \`transparent\` does not block time on the calendar and is equivalent to setting Show me as to Available in the Calendar UI.`,
        default: 'transparent',
      }),
    ),
    visibility: Type.Optional(
      StringEnum(['public', 'private'], {
        description: `The visibility of the event.
            - \`public\` is visible to all.
            - \`private\` is visible only to the calendar owner.`,
        default: 'public',
      }),
    ),
    conference: Type.Optional(CalendarEventConferenceRequestSchema),
    is_attendees_list_hidden: Type.Optional(Type.Boolean({ description: 'Is the attendees list hidden for attendees.' })),
    attendees: Type.Array(CalendarEventAttendeeInputSchema),
    recurrence: Type.Optional(
      Type.Array(Type.String(), {
        description: 'List of RRULE, EXRULE, RDATE and EXDATE lines for a recurring event, as specified in RFC5545.',
      }),
    ),
    start: Type.Union([CalendarEventDateTimeSchema, CalendarEventDateSchema], {
      description: 'The start date and time of the event. Use date for all day events.',
      discriminator: { propertyName: 'type' },
    }),
    end: Type.Union([CalendarEventDateTimeSchema, CalendarEventDateSchema], {
      description: 'The end date and time of the event. Use date for all day events.',
      discriminator: { propertyName: 'type' },
    }),
    notify: Type.Optional(
      StringEnum(['all', 'externalOnly', 'none'], {
        description: `Only available for google, guests to send updates to:
          - \`all\`: Notify all guests.
          - \`externalOnly\`: Notify only guests not on the calendar's domain.
          - \`none\`: Do not notify any guests.`,
        default: 'none',
      }),
    ),
    timezone: Type.Optional(
      Type.String({
        description: 'The IANA timezone the event is expressed in (e.g. `Europe/Paris`). Defaults to UTC when omitted.',
      }),
    ),
  },
  { additionalProperties: false },
);

export const UpdateCalendarEventParamsSchema = Type.Object({
  calendar_id: Type.String({ minLength: 1, description: 'Identifier of the parent calendar.' }),
  event_id: Type.String({ minLength: 1, description: 'ID of the event to update.' }),
});

export const UpdateCalendarEventBodySchema = Type.Object(
  {
    title: Type.Optional(Type.String({ description: 'The title of the event.' })),
    body: Type.Optional(Type.String({ description: 'The body of the event.' })),
    location: Type.Optional(Type.String({ description: 'The location of the event.' })),
    background_color: Type.Optional(
      Type.String({
        description: 'Background color of the calendar in hexadecimal format. For Google we match the closest palette available',
      }),
    ),
    transparency: Type.Optional(
      StringEnum(['opaque', 'transparent'], {
        description: `The transparency of the event.
            - \`opaque\` does block time on the calendar and is equivalent to setting Show me as to Busy in the Calendar UI.
            - \`transparent\` does not block time on the calendar and is equivalent to setting Show me as to Available in the Calendar UI.`,
      }),
    ),
    visibility: Type.Optional(
      StringEnum(['public', 'private'], {
        description: `The visibility of the event.
            - \`public\` is visible to all.
            - \`private\` is visible only to the calendar owner.`,
      }),
    ),
    conference: Type.Optional(
      Type.Union([CalendarEventConferenceRequestSchema, Type.Null()], {
        description: 'A conference to generate or attach, or `null` to remove the conference currently on the event.',
      }),
    ),
    is_attendees_list_hidden: Type.Optional(Type.Boolean({ description: 'Is the attendees list hidden for attendees.' })),
    attendees: Type.Optional(Type.Array(CalendarEventAttendeeInputSchema)),
    recurrence: Type.Optional(
      Type.Array(Type.String(), {
        description: 'List of RRULE, EXRULE, RDATE and EXDATE lines for a recurring event, as specified in RFC5545.',
      }),
    ),
    start: Type.Optional(
      Type.Union([CalendarEventDateTimeSchema, CalendarEventDateSchema], {
        description: 'The start date and time of the event.',
        discriminator: { propertyName: 'type' },
      }),
    ),
    end: Type.Optional(
      Type.Union([CalendarEventDateTimeSchema, CalendarEventDateSchema], {
        description: 'The end date and time of the event.',
        discriminator: { propertyName: 'type' },
      }),
    ),
    notify: Type.Optional(
      StringEnum(['all', 'externalOnly', 'none'], {
        description: `Only available for google, guests to send updates to:
      - \`all\`: Notify all guests.
      - \`externalOnly\`: Notify only guests not on the calendar's domain.
      - \`none\`: Do not notify any guests.`,
      }),
    ),
    timezone: Type.Optional(
      Type.String({
        description: 'The IANA timezone the event is expressed in (e.g. `Europe/Paris`). Defaults to UTC when omitted.',
      }),
    ),
  },
  { additionalProperties: false },
);

export const DeleteCalendarEventParamsSchema = Type.Object({
  calendar_id: Type.String({ minLength: 1, description: 'Identifier of the parent calendar.' }),
  event_id: Type.String({ minLength: 1, description: 'ID of the event to delete.' }),
});

export const SetCalendarEventRsvpParamsSchema = Type.Object({
  calendar_id: Type.String({ minLength: 1, description: 'Identifier of the parent calendar.' }),
  event_id: Type.String({ minLength: 1, description: 'ID of the event to respond to.' }),
});

export const SetCalendarEventRsvpBodySchema = Type.Object(
  {
    status: StringEnum(['yes', 'maybe', 'no'], {
      description: `The response of the connected account to the invitation.
          - \`yes\` accepts the invitation.
          - \`maybe\` accepts the invitation tentatively.
          - \`no\` declines the invitation.`,
    }),
    comment: Type.Optional(Type.String({ description: 'A comment sent to the organizer along with the response.' })),
  },
  { additionalProperties: false },
);

export const CancelCalendarEventParamsSchema = Type.Object({
  calendar_id: Type.String({ minLength: 1, description: 'Identifier of the parent calendar.' }),
  event_id: Type.String({ minLength: 1, description: 'ID of the event to cancel.' }),
});

export const CancelCalendarEventBodySchema = Type.Object(
  {
    comment: Type.Optional(
      Type.String({
        description: 'A message about the cancellation sent to the attendees. Only available for outlook.',
      }),
    ),
  },
  { additionalProperties: false },
);

export const RestoreCalendarEventParamsSchema = Type.Object({
  calendar_id: Type.String({ minLength: 1, description: 'Identifier of the parent calendar.' }),
  event_id: Type.String({ minLength: 1, description: 'ID of the event to restore.' }),
});

export type GetCalendarEventListParams = Static<typeof GetCalendarEventListParamsSchema>;
export type GetCalendarEventListQuery = Static<typeof GetCalendarEventListQuerySchema>;
export type GetCalendarEventParams = Static<typeof GetCalendarEventParamsSchema>;
export type CreateCalendarEventParams = Static<typeof CreateCalendarEventParamsSchema>;
export type CreateCalendarEventBody = Static<typeof CreateCalendarEventBodySchema>;
export type UpdateCalendarEventParams = Static<typeof UpdateCalendarEventParamsSchema>;
export type UpdateCalendarEventBody = Static<typeof UpdateCalendarEventBodySchema>;
export type DeleteCalendarEventParams = Static<typeof DeleteCalendarEventParamsSchema>;
export type CalendarEventAttendeeInput = Static<typeof CalendarEventAttendeeInputSchema>;
export type SetCalendarEventRsvpParams = Static<typeof SetCalendarEventRsvpParamsSchema>;
export type SetCalendarEventRsvpBody = Static<typeof SetCalendarEventRsvpBodySchema>;
export type CancelCalendarEventParams = Static<typeof CancelCalendarEventParamsSchema>;
export type CancelCalendarEventBody = Static<typeof CancelCalendarEventBodySchema>;
export type RestoreCalendarEventParams = Static<typeof RestoreCalendarEventParamsSchema>;
